import { Banknote, Clock, Landmark, Store, UserCircle2Icon } from 'lucide-vue-next'
import StatusBadge from 'pejer/src/components/StatusBadge.vue'
import ColumnLink from 'pejer/src/components/Table/core/ColumnLink.vue'
import { Payment, PaymentProviders } from 'pejer/src/tables/Payment'
import { h } from 'vue'
import { Groups, JSSchema } from '.'
import { Environment } from '../types/types'
import {
  Currency,
  CurrencyList,
  MoneyFormatter,
  PaymentEnvironmentFormatter,
  PaymentEnvironmentList,
  PaymentProviderList,
  PaymentStatusFormatter,
  PaymentStatuses,
} from './Global'
import { ColorPalette } from './helpers'

export namespace KidNorwayTransaction {
  export interface Full extends ListItem {}

  export interface ListItem {
    id: number
    environment: Environment
    paymentDate: string
    paymentReferenceID: string | null
    toBankAccount: string
    fromBankAccount: string
    amount: number
    kid: string
    insertTime: string
    invalidKIDRefund: {
      isAccepted: string
    }
    customer: {
      customerId: number
      customerExternalID?: string
      customerGuid: string
      customerType?: string
      email?: string
      mobile?: string
      mobilePrefix?: string
      name?: string
    }
    payment: {
      paymentStatus: Payment.PaymentStatus
      paymentStatusUpdateTime: string
      paymentProvider: PaymentProviders
      currency: Currency
      grossAmount: number
      insertTime: string
      description?: string
    }
    connectedAccount: {
      updateTime: string
      name?: string
      business_TaxID?: string
      countryCode?: string
      bban?: string
      iban?: string
      externalReferenceID?: string
      payoutsEnabled: boolean
      salesEnabled: boolean
      currency?: string
      bankName?: string
      businessName?: string
      businessType?: string
      website?: string
    }
  }
}

export const KidNorwayTransactionGroups = {
  Seller: {
    icon: Store,
    color: ColorPalette.pink,
    mainColumn: 'connectedAccount.name',
  },
  Customer: {
    icon: UserCircle2Icon,
    color: ColorPalette.blue,
    mainColumn: 'customer.name',
  },
  Payment: {
    icon: Banknote,
    color: ColorPalette.green,
    mainColumn: 'payment.description',
    isMainGroup: true,
  },
  'Payment details': {
    icon: Landmark,
    color: ColorPalette.purple,
    mainColumn: 'kid',
    isMainGroup: true,
  },
  'Gross amount': {
    icon: Banknote,
    color: ColorPalette.green,
    mainColumn: 'payment.grossAmount',
  },
  Time: {
    icon: Clock,
    color: ColorPalette.black,
    mainColumn: 'insertTime',
  },
} satisfies Groups<KidNorwayTransaction.ListItem>

export const KidNorwayTransactionListSchema: JSSchema<
  KidNorwayTransaction.ListItem,
  typeof KidNorwayTransactionGroups
> = {
  name: 'KidNorwayTransaction',
  title: 'Kid Norway Transactions',
  groups: KidNorwayTransactionGroups,
  fields: {
    paymentReferenceID: {
      type: 'string',
      optional: true,
      humanReadable: 'Reference ID',
      meta: {
        group: 'Payment',
      },
      formattedCell: (value: string) => {
        return h(
          ColumnLink,
          {
            content: value,
            id: value,
            pathName: 'payment',
          },
          () => value
        )
      },
    },
    fromBankAccount: {
      type: 'string',
      humanReadable: 'From Bank Account',
      meta: {
        group: 'Payment details',
      },
    },
    toBankAccount: {
      type: 'string',
      humanReadable: 'To Bank Account',
      meta: {
        group: 'Payment details',
      },
    },
    kid: {
      type: 'string',
      humanReadable: 'KID',
      meta: {
        group: 'Payment details',
      },
    },
    amount: {
      type: 'number',
      humanReadable: 'Paid amount',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Gross amount',
      },
    },
    paymentDate: {
      type: 'date',
      humanReadable: 'Payment Date',
      meta: {
        group: 'Payment',
      },
    },
    environment: {
      type: 'enum',
      humanReadable: 'Operating Environment',
      options: PaymentEnvironmentList,
      formatter: PaymentEnvironmentFormatter,
      meta: {
        group: 'Payment',
      },
    },
    invalidKIDRefund: {
      humanReadable: 'Invalid KID Refund Date',
      type: 'object',
      meta: {
        group: 'Payment',
      },
      values: {
        isAccepted: {
          type: 'date',
          humanReadable: 'Invalid KID refund initiated at',
          meta: {
            group: 'Payment',
          },
        },
      },
    },
    payment: {
      humanReadable: 'Payment',
      meta: {
        group: 'Payment',
      },
      type: 'object',
      values: {
        connectedAccountIDSeller: {
          humanReadable: 'Seller ID',
          relation: 'connectedAccounts.connectedAccountID',
          meta: {
            group: 'Seller',
          },
        },
        description: {
          type: 'string',
          humanReadable: 'Description',
          meta: {
            group: 'Payment',
          },
          sensitive: true,
        },
        externalObjectIDTag: {
          type: 'string',
          humanReadable: 'External Object ID Tag',
          meta: {
            group: 'Payment',
          },
        },
        environment: {
          type: 'enum',
          humanReadable: 'Operating Environment',
          options: PaymentEnvironmentList,
          formatter: PaymentEnvironmentFormatter,
          meta: {
            group: 'Payment',
          },
        },
        grossAmount: {
          type: 'number',
          humanReadable: 'Total Payment Amount',
          formatter: MoneyFormatter,
          metaType: 'money',
          meta: {
            group: 'Gross amount',
          },
        },
        paymentStatus: {
          type: 'enum',
          humanReadable: 'Status',
          options: PaymentStatuses,
          formatter: PaymentStatusFormatter,
          meta: {
            group: 'Payment',
          },
          formattedCell: (value: Payment.PaymentStatus) => {
            return h(StatusBadge, {
              status: value,
            })
          },
        },
        paymentStatusUpdateTime: {
          type: 'date',
          humanReadable: 'Payment Status Update Time',
          meta: {
            group: 'Payment',
          },
        },
        paymentProvider: {
          type: 'enum',
          humanReadable: 'Provider',
          options: PaymentProviderList,
          meta: {
            group: 'Payment',
          },
        },
        currency: {
          type: 'enum',
          humanReadable: 'Currency',
          options: CurrencyList,
          meta: {
            group: 'Payment',
          },
        },
      },
    },
    customer: {
      humanReadable: 'Customer',
      meta: {
        group: 'Customer',
      },
      type: 'object',
      values: {
        customerId: {
          humanReadable: 'ID',
          // relation: 'customers.customerId',
          meta: {
            group: 'Customer',
          },
        },
        customerExternalID: {
          humanReadable: 'External ID',
          relation: 'customers.customerExternalID',
          meta: {
            group: 'Customer',
          },
        },
        customerGuid: {
          type: 'string',
          humanReadable: 'GUID',
          meta: {
            group: 'Customer',
          },
        },
        customerType: {
          humanReadable: 'Type',
          relation: 'customers.customerType',
          meta: {
            group: 'Customer',
          },
        },
        email: {
          humanReadable: 'Email',
          relation: 'customers.email',
          meta: {
            group: 'Customer',
          },
          sensitive: true,
        },
        mobile: {
          humanReadable: 'Phone',
          relation: 'customers.mobile',
          meta: {
            group: 'Customer',
          },
          sensitive: true,
        },
        mobilePrefix: {
          humanReadable: 'Phone Prefix',
          relation: 'customers.mobilePrefix',
          meta: {
            group: 'Customer',
          },
        },
        name: {
          humanReadable: 'Full name',
          relation: 'customers.name',
          meta: {
            group: 'Customer',
          },
          specificCell:
            () =>
            ({ row }) => {
              return h(
                ColumnLink,
                {
                  content: row.original.customer?.name,
                  id: row.original.customer?.customerId,
                  pathName: 'customer',
                },
                () => row.original.customer?.name
              )
            },
          sensitive: true,
        },
      },
    },
    connectedAccount: {
      humanReadable: 'Seller',
      meta: {
        group: 'Seller',
      },
      type: 'object',
      values: {
        name: {
          humanReadable: 'Account Name',
          relation: 'connectedAccounts.name',
          meta: {
            group: 'Seller',
          },
          specificCell:
            () =>
            ({ row }) => {
              return h(
                ColumnLink,
                {
                  content: row.original.connectedAccount?.name,
                  id: row.original.payment?.connectedAccountIDSeller,
                  pathName: 'account',
                },
                () => row.original.connectedAccount?.name
              )
            },
          sensitive: true,
        },
        bban: {
          relation: 'connectedAccounts.bban',
          meta: {
            group: 'Seller',
          },
          sensitive: true,
        },
        iban: {
          relation: 'connectedAccounts.iban',
          meta: {
            group: 'Seller',
          },
          sensitive: true,
        },
        externalReferenceID: {
          relation: 'connectedAccounts.externalReferenceID',
          meta: {
            group: 'Seller',
          },
        },
        payoutsEnabled: {
          humanReadable: 'Payouts Enabled',
          relation: 'connectedAccounts.payoutsEnabled',
          meta: {
            group: 'Seller',
          },
        },
        salesEnabled: {
          humanReadable: 'Sales Enabled',
          relation: 'connectedAccounts.salesEnabled',
          meta: {
            group: 'Seller',
          },
        },
        currency: {
          humanReadable: 'Currency',
          relation: 'connectedAccounts.currency',
          meta: {
            group: 'Seller',
          },
        },
        bankName: {
          relation: 'connectedAccounts.bankName',
          meta: {
            group: 'Seller',
          },
        },
        businessName: {
          relation: 'connectedAccounts.businessName',
          meta: {
            group: 'Seller',
          },
          sensitive: true,
        },
        business_TaxID: {
          relation: 'connectedAccounts.business_TaxID',
          meta: {
            group: 'Seller',
          },
        },
        countryCode: {
          relation: 'connectedAccounts.countryCode',
          meta: {
            group: 'Seller',
          },
        },
        businessType: {
          relation: 'connectedAccounts.businessType',
          meta: {
            group: 'Seller',
          },
        },
        website: {
          humanReadable: 'Website',
          relation: 'connectedAccounts.website',
          meta: {
            group: 'Seller',
          },
          sensitive: true,
        },
      },
    },
    insertTime: {
      type: 'date',
      humanReadable: 'Created at',
      meta: {
        group: 'Time',
      },
      isDefaultSortColumn: true,
    },
  },
  actions: {},
}

export const KidNorwayTransactionFullSchema: JSSchema<
  KidNorwayTransaction.Full,
  typeof KidNorwayTransactionGroups
> = {
  ...KidNorwayTransactionListSchema,
  title: 'Kid Norway Transaction',
}
